import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {
  captureDirectTurnTiming,
  createPreparedPrompt,
  type DirectTurnSurface,
  type DirectTurnTelemetrySeed,
} from '@github-ui/ahp-session-client/contracts'
import {useGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {referenceURL} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage, type SavedMessageSource} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {scheduleAfterNextPaint} from '@github-ui/copilot-chat-state/schedule-after-next-paint'
import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {
  type AgentDropdownRepository,
  type CopilotChatCommandType,
  type CopilotChatMode,
  type CopilotChatReference,
  type CopilotChatRepo,
  ModeSupportName,
  type TaskRequestReference,
} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {
  type ComposerAttachmentsController,
  useComposerAttachments,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-attachments'
import {useLastUsedRepository} from '@github-ui/copilot-shared-ui/repos/use-last-used-repository'
import {sendEvent} from '@github-ui/hydro-analytics'
import {dispatchEnvironmentSubmission} from '@github-ui/one-copilot-composer/environment-strip/dispatch-environment-submission'
import type {EnvironmentOption} from '@github-ui/one-copilot-composer/environment-strip/environment-option'
import {
  type EnvironmentSubmit,
  useEnvironmentSelection,
  useSelectedRepositorySandboxPolicy,
} from '@github-ui/one-copilot-composer/use-environment-selection'
import {useLastSelectedAgent} from '@github-ui/one-copilot-composer/use-last-selected-agent'
import {useLocation} from '@github-ui/react-router'
import safeStorage from '@github-ui/safe-storage'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {useSafeTimeout} from '@primer/react'
import throttle from 'lodash-es/throttle'
import type React from 'react'
import type {FormEvent, RefObject} from 'react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import type {CustomModelSelectionState} from '../plugin/copilot-immersive-plugin'
import {extractChatCommandType} from '../utils/chat-commands-helpers'
import {executePotentialSlashCommand} from '../utils/copilot-slash-commands'
import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import type {AvailableCommand} from './use-available-commands'

// Legacy JS autosize bounds (used only when `copilot_css_textarea_autosize` is disabled).
const MAX_HEIGHT = 300
const MIN_HEIGHT = 56
const SEARCH_PROMPT_STORAGE_KEY = 'copilot-chat-search-prompt'
const searchPromptStorage = safeStorage('sessionStorage', {throwQuotaErrorsOnSet: false, ttl: 30_000})

interface UseChatInputProps {
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  onSubmit?: (text: string, additionalReferences?: CopilotChatReference[], event?: Event) => Promise<void>
  isLoading?: boolean
  clearPastedFile?: () => void
  onChatCommandChange?: (command: CopilotChatCommandType | null) => void
  isInPortal?: boolean
  shouldClearTextOnCommandSubmit?: boolean
  shouldClearTextOnSubmit?: boolean
  canSubmitWithEmptyText?: boolean
  availableCommands?: AvailableCommand[]
  activeModeName?: string
  storageKeySuffix?: string
  initialText?: string
  /** Maximum height for the textarea input in pixels. Overrides the default MAX_HEIGHT (legacy JS autosize only). */
  maxTextareaHeight?: number
  /** Source of the saved message, used to differentiate messages from different experiences */
  source?: SavedMessageSource
  getAdditionalSubmitText?: () => string
  clearAdditionalSubmitContent?: () => void
  onEnvironmentSubmit?: EnvironmentSubmit
  supportsManagedSandbox?: boolean
  directTurnTelemetrySurface?: DirectTurnSurface
}

interface UseChatInputReturn {
  /** true if the user has provided input since the last message was sent */
  typingHasStarted: boolean
  /** reference to the input textarea */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** reference to the textarea preview div, which is used to display rich text in the input */
  textareaPreviewRef: RefObject<HTMLDivElement | null>
  /** container whose height can be adjusted to match the user's input */
  textAreaPreviewContainerRef: RefObject<HTMLDivElement | null>
  /** the current user input */
  text: string
  /** sets the contents of the input */
  setText: React.Dispatch<React.SetStateAction<string>>
  /** loading state during agent task submission before navigation */
  isAgentTaskSubmitInProgress: boolean
  /** the current chat command extracted from the input, or null if there isn't one */
  command: CopilotChatCommandType | null
  /** sets the current chat command */
  setCommand: React.Dispatch<React.SetStateAction<CopilotChatCommandType | null>>
  /** function to call to send a message */
  handleSubmit: (e?: FormEvent, interactionEvent?: Event) => Promise<void>
  /** function to call to send a message to a new thread */
  handleSubmitToNewThread: () => Promise<void>
  /** handler for the onScroll event of the input textarea */
  handleScroll: () => void
  /** handler for the onChange event of the input textarea */
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** call to stop response streaming */
  handleStop: (onAccepted?: () => void) => Promise<void>
  /** whether the current references allow an otherwise empty prompt to be submitted */
  hasSendableReferences: boolean
  /** handler to move cursor to the end of prompt loaded from query params */
  handleFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  /** most recently used repo as a reference or for agent tasks */
  lastUsedRepo: CopilotChatRepo | null
  setLastUsedRepo: (repo: CopilotChatRepo | null) => void
  /**
   * agent tasks
   */
  selectedRepo: AgentDropdownRepository | undefined
  setSelectedRepo: (repo: AgentDropdownRepository | undefined) => void
  selectedBranch: string | undefined
  setSelectedBranch: (branch: string | undefined) => void
  selectedAgent: SelectedAgent
  setSelectedAgent: (agent: SelectedAgent) => void
  selectedAgentModelSelection: CustomModelSelectionState
  setSelectedAgentModelSelection: React.Dispatch<React.SetStateAction<CustomModelSelectionState>>
  initialRepoNWO?: string
  initialText?: string
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: React.Dispatch<React.SetStateAction<EnvironmentOption | null>>
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  isSandboxPolicyPending: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  /** Inline attachments sent with an AHP prompt. Unused on the cloud task path. */
  environmentAttachments: ComposerAttachmentsController
}

/**
 * Helper function to determine if the current mode is an agent command.
 */
export function isAgentCommand(activeModeName: string | undefined): boolean {
  return activeModeName === ModeSupportName.Agent
}

/**
 * Does a bunch of stuff, too much really, to manage a chat input for a copilot chat.
 */
export function useChatInput(props: UseChatInputProps): UseChatInputReturn {
  const state = useChatStateValues('selectedThreadID', 'mode', 'autoSubmit', 'model', 'currentReferences', 'messages')
  const getChatState = useGetChatState()
  const manager = useChatManager()
  const navigate = useNavigate()
  const {selectedThreadID: contextThreadID, mode, autoSubmit, model} = state
  const {
    activeModeName,
    clearPastedFile,
    isLoading,
    onSubmit,
    onChatCommandChange,
    isInPortal,
    shouldClearTextOnCommandSubmit = true,
    shouldClearTextOnSubmit = true,
    canSubmitWithEmptyText = false,
    availableCommands = [],
    storageKeySuffix,
    source = 'immersive',
    getAdditionalSubmitText,
    clearAdditionalSubmitContent,
    onEnvironmentSubmit,
    supportsManagedSandbox,
    directTurnTelemetrySurface,
  } = props
  // hijack the selectedThreadID if storageKeySuffix is provided. This works for agents since agents
  // entrypoints are not using the thread methods in this file. The cleaner solution would be a bigger
  // refactor.
  const selectedThreadID = storageKeySuffix ?? contextThreadID
  const savedUserMessage = copilotLocalStorage.getSavedMessage(selectedThreadID, source)
  const savedUserMessageOnError = copilotLocalStorage.getSavedUserMessageOnError(selectedThreadID)
  const {pathname} = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [text, setText] = useState(() => {
    if (props.initialText) {
      return props.initialText
    }

    // we figure out the initial text here so it will be available in the first render
    const prompt = getPromptFromSearchParams(state.mode, pathname, searchParams)
    return prompt ?? savedUserMessageOnError ?? savedUserMessage ?? ''
  })
  const [isAgentTaskSubmitInProgress, setIsAgentTaskSubmitInProgress] = useState(false)
  const agentTaskSubmissionRef = useRef(false)
  const [command, setCommand] = useState<CopilotChatCommandType | null>(null)

  const [typingHasStarted, setTypingHasStarted] = useState(false)
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const textAreaRef = props.textAreaRef || internalRef
  // Mirror the active textarea node into a ref the compiler recognizes as a ref. Because
  // `textAreaRef` is `props.textAreaRef || internalRef`, the compiler can't prove it's a ref and
  // would otherwise track `textAreaRef.current` as a reactive dependency of any callback that reads
  // it. Reading this synced ref instead keeps those callbacks memoizable.
  const textAreaNodeRef = useRef<HTMLTextAreaElement | null>(null)
  const textareaPreviewRef = useRef<HTMLDivElement>(null)
  const textAreaPreviewContainerRef = useRef<HTMLDivElement>(null)
  const recallIndexRef = useRef(0)

  const reloadThreadTimerRef = useRef<number | null>(null)
  // Store manager callback, which will be called when state changes
  const reloadThreadFetchMessagesCallbackRef = useRef(() => {})
  const handleAutoSubmitRef = useRef(false)
  const prevThreadIDRef = useRef<string | null>(selectedThreadID)
  const {safeSetTimeout} = useSafeTimeout()
  const prevContainerHeightRef = useRef(0)
  const autosizeFrameRef = useRef<number | null>(null)

  // creating a task
  const [lastUsedRepo, setLastUsedRepo] = useLastUsedRepository()

  const [selectedRepo, setSelectedRepo] = useState<AgentDropdownRepository | undefined>(undefined)
  const selectedRepoRef = useRef<AgentDropdownRepository | undefined>(undefined)

  const [initialTaskRepositorySelection] = useState(() => copilotLocalStorage.getTaskRepositorySelection())
  const repoReference = state.currentReferences.find(ref => ref.type === 'repository')
  const initialRepoNWO =
    initialTaskRepositorySelection?.nameWithOwner ??
    (repoReference
      ? `${repoReference.ownerLogin}/${repoReference.name}`
      : lastUsedRepo
        ? `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`
        : undefined)

  const onSelectRepo = useCallback((repo: AgentDropdownRepository | undefined) => {
    selectedRepoRef.current = repo
    setSelectedRepo(repo)
    copilotLocalStorage.setTaskRepositorySelection(
      repo ? {nameWithOwner: repo.nameWithOwner, branch: repo.defaultBranch} : null,
    )
  }, [])
  const [selectedBranch, setSelectedBranchState] = useState<string | undefined>(() =>
    initialTaskRepositorySelection && initialTaskRepositorySelection.nameWithOwner === initialRepoNWO
      ? initialTaskRepositorySelection.branch
      : undefined,
  )
  const setSelectedBranch = useCallback((branch: string | undefined) => {
    setSelectedBranchState(branch)
    if (selectedRepoRef.current) {
      copilotLocalStorage.setTaskRepositorySelection({nameWithOwner: selectedRepoRef.current.nameWithOwner, branch})
    }
  }, [])

  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent()
  const [selectedAgentModelSelection, setSelectedAgentModelSelection] = useState<CustomModelSelectionState>({
    isReady: false,
  })
  const {supportsSandbox: supportsSelectedRepositorySandbox, isPending: isSandboxPolicyPending} =
    useSelectedRepositorySandboxPolicy({
      selectedRepositoryId: selectedRepo?.id,
      initialRepositoryId: undefined,
      supportsManagedSandbox: supportsManagedSandbox ?? false,
    })
  const {
    selectedEnvironment,
    setSelectedEnvironment,
    isAhpEnvironment,
    isSandboxEnvironment,
    showSandboxOption,
    showEnvironmentPicker,
  } = useEnvironmentSelection({
    onEnvironmentSubmit,
    supportsManagedSandbox: supportsSelectedRepositorySandbox,
    sandboxPolicyPending: isSandboxPolicyPending,
  })
  const environmentAttachments = useComposerAttachments()

  // Keep the recognized node ref in sync with the active textarea after every commit so event
  // handlers can read it without forcing the compiler to track `textAreaRef.current`.
  useEffect(() => {
    textAreaNodeRef.current = textAreaRef.current
  })

  const restoreTextAreaFocus = useCallback(() => {
    window.setTimeout(() => textAreaNodeRef.current?.focus(), 0)
  }, [])

  useEffect(() => {
    if (selectedThreadID === prevThreadIDRef.current) {
      copilotLocalStorage.setSavedMessage(selectedThreadID, source, text)
    } else {
      setText(copilotLocalStorage.getSavedMessage(selectedThreadID, source) || '')
      prevThreadIDRef.current = selectedThreadID
    }
  }, [selectedThreadID, source, text])

  // Legacy JS autosize: measures `scrollHeight` and writes explicit heights on every keystroke.
  // Disabled when `copilot_css_textarea_autosize` is on, where the browser sizes the input via CSS
  // grid-stacking + `field-sizing` (see ChatInput.module.css).
  useLayoutEffect(() => {
    if (copilotFeatureFlags.cssTextareaAutosize) return

    let tries = 0

    function adjustHeight() {
      if (!textAreaRef.current || !textAreaPreviewContainerRef.current || !textareaPreviewRef.current) return

      tries++
      if ((isInPortal || mode === 'assistive') && textAreaRef.current.scrollHeight === 0 && tries < 10) {
        // because this is (often) in a Portal, useLayoutEffect() can run before we're inserted in the DOM, and we are
        // thus unable to get a correct scrollHeight. in that case, let's try again in a bit
        safeSetTimeout(adjustHeight, 1)
        return
      }

      if (text.length === 0 && prevContainerHeightRef.current === MIN_HEIGHT) {
        return
      }

      textAreaRef.current.style.height = '0'
      const scrollHeight = Math.max(textAreaRef.current.scrollHeight, MIN_HEIGHT)
      const maxHeight = props.maxTextareaHeight ?? MAX_HEIGHT
      const containerHeight = Math.min(scrollHeight, maxHeight)

      textareaPreviewRef.current.style.height = `${scrollHeight}px`
      textAreaRef.current.style.height = `${scrollHeight}px`

      // Only write the container height when it changed. This is the write that invalidates the
      // surrounding layout, so skipping it when nothing changed keeps typing cheap.
      if (containerHeight !== prevContainerHeightRef.current) {
        textAreaPreviewContainerRef.current.style.height = `${containerHeight}px`
        // Override CSS max-height when a custom maxTextareaHeight is provided, or reset it when not
        textAreaPreviewContainerRef.current.style.maxHeight = props.maxTextareaHeight
          ? `${props.maxTextareaHeight}px`
          : ''

        prevContainerHeightRef.current = containerHeight
      }
    }

    // Resizing the textarea changes its height, reads the new scrollHeight,
    // then writes the height back. If we do all of that before the current
    // frame paints, it delays the paint and hurts INP. That means the typed
    // character can feel sluggish. So we let the browser paint first so the
    // new character shows up quickly. We resize in the next frame so the
    // textarea height still matches the new text without much delay.
    return scheduleAfterNextPaint(autosizeFrameRef, adjustHeight)
  }, [mode, safeSetTimeout, text, textAreaRef, textareaPreviewRef, isInPortal, activeModeName, props.maxTextareaHeight])

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // keep the current text focus
    if (e.currentTarget.selectionStart > 0) {
      return
    }
    // else set focus to end of text
    e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
  }

  const handleScroll = useCallback(() => {
    if (!textAreaNodeRef.current || !textareaPreviewRef.current || !textAreaPreviewContainerRef.current) return

    requestAnimationFrame(() => {
      if (!textAreaNodeRef.current || !textareaPreviewRef.current) return
      textareaPreviewRef.current.scrollTop = textAreaNodeRef.current.scrollTop
      textareaPreviewRef.current.scrollLeft = textAreaNodeRef.current.scrollLeft
    })
  }, [])

  // Throttle to run at most once per frame (~60fps).
  const throttledHandleScroll = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs
      throttle(handleScroll, 16, {leading: true, trailing: true}),
    [handleScroll],
  )

  useEffect(() => {
    return () => throttledHandleScroll.cancel()
  }, [throttledHandleScroll])

  const sendableReferences = useMemo(
    () => state.currentReferences.filter(ref => !('isClientOnly' in ref) || !ref.isClientOnly),
    [state.currentReferences],
  )

  const handleSubmit = useCallback(
    async (e?: FormEvent, interactionEvent?: Event) => {
      e?.preventDefault()

      const isAgent = isAgentCommand(activeModeName)
      const activeCommand = availableCommands.find(
        availableCommand => availableCommand.key === command || availableCommand.mode?.name === activeModeName,
      )
      if (
        isAgent &&
        !isAhpEnvironment &&
        activeCommand?.requiresModelSelectionReady?.(getChatState()) &&
        !selectedAgentModelSelection.isReady
      ) {
        return
      }
      if (isAgent) {
        if (agentTaskSubmissionRef.current) return
        agentTaskSubmissionRef.current = true
        setIsAgentTaskSubmitInProgress(true)
      }

      setTypingHasStarted(false)
      const directTurnTelemetrySeed: DirectTurnTelemetrySeed | undefined =
        isAgent && isSandboxEnvironment && directTurnTelemetrySurface
          ? {
              requestId: crypto.randomUUID(),
              timing: captureDirectTurnTiming(),
              surface: directTurnTelemetrySurface,
            }
          : undefined

      // Clear all previously saved messages
      copilotLocalStorage.clearSavedUserMessage(selectedThreadID, source)
      copilotLocalStorage.clearSavedUserMessageOnError(selectedThreadID)

      const currentChatState = getChatState()
      if (currentChatState.defaultRecipient && !text.includes(`@${currentChatState.defaultRecipient}`)) {
        manager.clearDefaultRecipient()
      }

      // Save the selected repository to localStorage when agent task is created
      if (isAgent && selectedRepo?.id != null) {
        try {
          const result = await manager.service.fetchRepo(selectedRepo.id)
          if (result.ok && result.payload) {
            setLastUsedRepo(result.payload)
          }
        } catch (error) {
          agentTaskSubmissionRef.current = false
          setIsAgentTaskSubmitInProgress(false)
          restoreTextAreaFocus()
          throw error
        }
      }

      // Serialize file/snippet references to include in the agent task prompt
      const serializedReferenceURLs = isAgent
        ? sendableReferences
            .filter(ref => ref.type === 'file' || ref.type === 'snippet')
            .map(ref => referenceURL(ref))
            .filter(url => url !== '#' && url !== '')
        : []

      // In agent mode, prepend additional content (e.g. code blocks) to the text before passing to
      // executePotentialSlashCommand so pasted blocks are included; in non-agent mode, wrappedOnSubmit handles this.
      const additional = isAgent ? (getAdditionalSubmitText?.() ?? '') : ''
      const effectiveInput = additional ? (text.trim() ? `${additional}\n${text}` : additional) : text
      const environmentPrompt =
        serializedReferenceURLs.length > 0
          ? `${effectiveInput}\n\n${serializedReferenceURLs.join('\n')}`
          : effectiveInput

      if (isAgent && isAhpEnvironment) {
        const canSubmitToEnvironment =
          !!onEnvironmentSubmit &&
          !!selectedEnvironment?.environmentId &&
          (!isSandboxEnvironment || (!!selectedRepo && supportsSelectedRepositorySandbox))
        if (!canSubmitToEnvironment) {
          agentTaskSubmissionRef.current = false
          setIsAgentTaskSubmitInProgress(false)
          return
        }

        const {modelId, reasoningEffort} = selectedAgentModelSelection
        const promptModel = modelId
          ? {
              id: modelId,
              ...(copilotFeatureFlags.codingAgentReasoningEffort && reasoningEffort ? {config: {reasoningEffort}} : {}),
            }
          : undefined
        const attachmentSnapshot = environmentAttachments.snapshot()

        let dispatched = false
        try {
          dispatched = await dispatchEnvironmentSubmission({
            selectedEnvironment,
            onEnvironmentSubmit,
            prompt: createPreparedPrompt({
              text: environmentPrompt,
              attachments: attachmentSnapshot.attachments,
              ...(promptModel ? {model: promptModel} : {}),
            }),
            ...(selectedRepo?.nameWithOwner ? {repositoryNwo: selectedRepo.nameWithOwner} : {}),
            ...(selectedBranch ? {baseRef: selectedBranch} : {}),
            ...(directTurnTelemetrySeed ? {directTurnTelemetrySeed} : {}),
          })
        } catch {
          agentTaskSubmissionRef.current = false
          setIsAgentTaskSubmitInProgress(false)
          restoreTextAreaFocus()
          return
        }
        agentTaskSubmissionRef.current = false
        setIsAgentTaskSubmitInProgress(false)
        if (!dispatched) return

        attachmentSnapshot.clear()
        clearAdditionalSubmitContent?.()
        if (shouldClearTextOnCommandSubmit) {
          setText('')
        } else {
          setText(`Creating ${text}...`)
        }
        return
      }

      let ranSlashCommand = false
      try {
        ranSlashCommand = await executePotentialSlashCommand(
          effectiveInput,
          currentChatState,
          manager,
          navigate,
          availableCommands,
          activeModeName,
          isAgent
            ? {
                selectedRepo,
                selectedBranch,
                selectedAgent,
                selectedModelId: selectedAgentModelSelection.modelId,
                selectedReasoningEffort: selectedAgentModelSelection.reasoningEffort,
                references: serializedReferenceURLs.length > 0 ? serializedReferenceURLs : undefined,
              }
            : {},
        )
      } catch (error) {
        if (isAgent) {
          agentTaskSubmissionRef.current = false
          setIsAgentTaskSubmitInProgress(false)
          restoreTextAreaFocus()
          return
        }
        throw error
      }
      if (isAgent) {
        agentTaskSubmissionRef.current = false
        setIsAgentTaskSubmitInProgress(false)
      }
      if (ranSlashCommand) {
        if (text.trim() === '/new') {
          copilotLocalStorage.setSavedMessageFast(selectedThreadID, source, null)
        }

        clearAdditionalSubmitContent?.()

        if (shouldClearTextOnCommandSubmit) {
          setText('')
        } else {
          const textAfterCommand = text.substring(text.indexOf(' ') + 1).trim()
          setText(`Creating ${textAfterCommand}...`)
        }
        return
      }

      if (!isLoading) {
        let messageToSubmit = text
        if (!messageToSubmit) {
          if (canSubmitWithEmptyText) {
            messageToSubmit = ''
          } else {
            // get references from state
            const refs = sendableReferences
            const [inferred, newMessage] = manager.tryInferMessage(refs)
            if (!inferred || !newMessage) {
              return
            } else {
              messageToSubmit = newMessage
            }
          }
        }
        if (activeModeName === ModeSupportName.CreateIssue) {
          messageToSubmit = `/create-issue ${messageToSubmit}`
        }
        try {
          const showsTaskSubmitProgress = isAgent || !shouldClearTextOnSubmit
          if (shouldClearTextOnSubmit) {
            setText('')
          } else {
            setText(prev => `Creating ${prev}...`)
          }
          if (showsTaskSubmitProgress) {
            if (isAgent) {
              agentTaskSubmissionRef.current = true
            }
            setIsAgentTaskSubmitInProgress(true)
          }
          const additionalReferences: CopilotChatReference[] = []

          // Build task request reference if this is an agent command
          if (isAgent && selectedRepo) {
            const descriptionWithRefs =
              serializedReferenceURLs.length > 0
                ? `${messageToSubmit}\n\n${serializedReferenceURLs.join('\n')}`
                : messageToSubmit
            const taskRequestReference: TaskRequestReference = {
              type: 'task-request',
              description: descriptionWithRefs,
              repoOwner: selectedRepo.owner,
              repoName: selectedRepo.nameOnly,
              ref: selectedBranch || selectedRepo.defaultBranch,
              customAgent: selectedAgent.subAgentSlug,
              agentId: selectedAgent.topLevelAgentId,
            }
            additionalReferences.push(taskRequestReference)
          }

          const submittedReferences = additionalReferences.length > 0 ? additionalReferences : undefined
          await onSubmit?.(messageToSubmit, submittedReferences, interactionEvent)

          if (showsTaskSubmitProgress) {
            if (isAgent) {
              agentTaskSubmissionRef.current = false
            }
            setIsAgentTaskSubmitInProgress(false)
          }
          if (!shouldClearTextOnSubmit) {
            copilotLocalStorage.clearSavedUserMessage(selectedThreadID, source)
          }
        } catch (error) {
          // On error, stop any loading states that disables the submit button
          if (isAgent) {
            agentTaskSubmissionRef.current = false
          }
          setIsAgentTaskSubmitInProgress(false)
          // When shouldClearTextOnSubmit is false we replaced the input with a "Creating ..."
          // progress label, so on failure restore the user's original text to edit/retry.
          // Validation errors also restore the text (the onSubmit callback surfaces the message).
          if (!shouldClearTextOnSubmit || error instanceof ChatInputValidationError) {
            setText(messageToSubmit)
          }
          if (isAgent) {
            restoreTextAreaFocus()
          }
          if (error instanceof ChatInputValidationError) {
            // Don't re-throw validation errors to avoid console errors - the onSubmit callback should handle displaying them
            return
          }
          // Re-throw unexpected errors as they indicate real problems that need to be logged/handled
          throw error
        }
      }
    },
    [
      selectedThreadID,
      source,
      text,
      activeModeName,
      command,
      getChatState,
      selectedRepo,
      manager,
      navigate,
      availableCommands,
      selectedBranch,
      selectedAgent,
      selectedAgentModelSelection,
      isLoading,
      setLastUsedRepo,
      shouldClearTextOnCommandSubmit,
      sendableReferences,
      shouldClearTextOnSubmit,
      canSubmitWithEmptyText,
      onSubmit,
      getAdditionalSubmitText,
      clearAdditionalSubmitContent,
      onEnvironmentSubmit,
      directTurnTelemetrySurface,
      selectedEnvironment,
      isAhpEnvironment,
      isSandboxEnvironment,
      supportsSelectedRepositorySandbox,
      environmentAttachments,
      restoreTextAreaFocus,
    ],
  )

  const handleSubmitToNewThread = useCallback(
    () =>
      manager.sendMessageToNewThread(selectedThreadID, text, sendableReferences, getChatState().context, model, source),
    [manager, selectedThreadID, text, sendableReferences, getChatState, model, source],
  )

  useEffect(() => {
    const handlePrompt = async () => {
      const prompt = getPromptFromSearchParams(mode, pathname, searchParams)
      const updatedSearchParams = new URLSearchParams(searchParams)
      const queryModel = updatedSearchParams.get('model')
      const modelAvailable = !queryModel || queryModel === model.id
      const isAgent = isAgentCommand(activeModeName)
      const activeCommand = availableCommands.find(
        availableCommand => availableCommand.key === command || availableCommand.mode?.name === activeModeName,
      )
      const isModelSelectionReady =
        !isAgent ||
        isAhpEnvironment ||
        !activeCommand?.requiresModelSelectionReady?.(getChatState()) ||
        selectedAgentModelSelection.isReady

      if (prompt && modelAvailable && !handleAutoSubmitRef.current) {
        const isTrustedSearchPrompt =
          isImmersivePromptPrefillPath(mode, pathname) &&
          searchPromptStorage.getItem(SEARCH_PROMPT_STORAGE_KEY) === prompt
        const shouldAutoSubmit =
          shouldAutoSubmitPromptFromSearchParams(mode, pathname, autoSubmit === true) || isTrustedSearchPrompt
        if (shouldAutoSubmit && prompt.trim().length > 0 && !isModelSelectionReady) {
          return
        }
        searchPromptStorage.removeItem(SEARCH_PROMPT_STORAGE_KEY)

        updatedSearchParams.delete('prompt')
        updatedSearchParams.delete('model')
        updatedSearchParams.delete('autosubmit')
        sendEvent('dotcom_chat.activate', {target: 'COPILOT_PROMPT_LOADED_FROM_URL', mode})
        setSearchParams(updatedSearchParams, {replace: true})
        if (shouldAutoSubmit && prompt.trim().length > 0) {
          handleAutoSubmitRef.current = true
          await Promise.resolve()
          await handleSubmit()
        }
      } else if (searchParams.get('autosubmit')) {
        updatedSearchParams.delete('autosubmit')
        setSearchParams(updatedSearchParams, {replace: true})
      }
    }
    void handlePrompt()
  }, [
    pathname,
    searchParams,
    setSearchParams,
    mode,
    autoSubmit,
    model,
    handleSubmit,
    activeModeName,
    command,
    availableCommands,
    selectedAgentModelSelection.isReady,
    isAhpEnvironment,
    getChatState,
  ])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTypingHasStarted(true)

      if (!textAreaNodeRef.current) return

      const newValue = e.target.value

      // Critical: Update text immediately for responsive typing
      setText(newValue)

      clearPastedFile?.()
      recallIndexRef.current = 0

      // Scroll can be throttled
      throttledHandleScroll()
    },
    [throttledHandleScroll, clearPastedFile],
  )

  const handleStop = useCallback(
    async (onAccepted?: () => void) => {
      await manager.stopStreaming()
      onAccepted?.()

      // Because the request didn't complete, the client only has the local/ephemeral copy of the streamed copilot response,
      // which doesn't have the real message id.
      // Re-fetch the messages from the server so that we have the final state with the correct message ids.
      if (selectedThreadID != null) {
        // Block new messages or retries from being sent while we're reloading the thread
        manager.startThreadReload()
        // Wait 6 seconds for server to complete the saving of the message
        // This matches server-side cancellation logic timing
        await new Promise<string>(timerDone => {
          reloadThreadTimerRef.current = setTimeout(() => timerDone(selectedThreadID), 6 * 1000) as unknown as number
        })
        reloadThreadFetchMessagesCallbackRef.current = await manager.fetchMessages(selectedThreadID, true, true)
      }
    },
    [manager, selectedThreadID],
  )

  // Once messages change in state, unblock registered fetchMessages await
  useEffect(() => {
    if (reloadThreadFetchMessagesCallbackRef.current) {
      reloadThreadFetchMessagesCallbackRef.current()
      reloadThreadFetchMessagesCallbackRef.current = () => {}
    }
  }, [state.messages])

  useEffect(() => {
    // Cleanup function to clear the reload timer on unmount or re-render
    return () => {
      // Return cleanup function - when component unmounts, stop the timer so that it doesn't try to reload the old thread
      if (reloadThreadTimerRef.current) {
        clearTimeout(reloadThreadTimerRef.current)
        reloadThreadTimerRef.current = null
      }
      // Also cancel the fetchMessages callbacks
      manager.cancelThreadReload()
      reloadThreadFetchMessagesCallbackRef.current = () => {}
    }
  }, [manager])

  useEffect(() => {
    const newCommand = extractChatCommandType(
      text,
      availableCommands.map(cmd => cmd.key),
    )
    if (command !== newCommand) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCommand(newCommand)
      onChatCommandChange?.(newCommand)
    }
  }, [text, command, onChatCommandChange, availableCommands])

  return {
    typingHasStarted,
    textAreaRef,
    textareaPreviewRef,
    textAreaPreviewContainerRef,
    text,
    setText,
    isAgentTaskSubmitInProgress,
    command,
    setCommand,
    handleSubmit,
    handleSubmitToNewThread,
    handleScroll: throttledHandleScroll,
    handleChange,
    handleStop,
    handleFocus,
    lastUsedRepo,
    setLastUsedRepo,

    // task
    selectedRepo,
    setSelectedRepo: onSelectRepo,
    selectedBranch,
    setSelectedBranch,
    selectedAgent,
    setSelectedAgent,
    selectedAgentModelSelection,
    setSelectedAgentModelSelection,
    initialRepoNWO,
    selectedEnvironment,
    setSelectedEnvironment,
    isAhpEnvironment,
    isSandboxEnvironment,
    isSandboxPolicyPending,
    showSandboxOption,
    showEnvironmentPicker,
    hasSendableReferences: sendableReferences.length > 0,
    environmentAttachments,
  }
}

function getPromptFromSearchParams(mode: CopilotChatMode, pathname: string, searchParams: URLSearchParams) {
  const isImmersivePromptPrefillPage = isImmersivePromptPrefillPath(mode, pathname)
  const isRepoAgentsPage = isAssistiveRepoAgentsPath(mode, pathname)

  if (isImmersivePromptPrefillPage || isRepoAgentsPage) {
    // Load prompt from URL if it exists
    const prompt = searchParams.get('prompt')
    if (prompt) {
      return prompt
    }
  }
  return undefined
}

function shouldAutoSubmitPromptFromSearchParams(mode: CopilotChatMode, pathname: string, payloadAutoSubmit: boolean) {
  if (!isImmersivePromptPrefillPath(mode, pathname)) return false
  return payloadAutoSubmit
}

function isImmersivePromptPrefillPath(mode: CopilotChatMode, pathname: string) {
  return mode === 'immersive' && /^\/copilot(\/agents|\/spaces\/[a-zA-Z0-9-_]+\/[0-9]+)?\/?$/.test(pathname)
}

function isAssistiveRepoAgentsPath(mode: CopilotChatMode, pathname: string) {
  return mode === 'assistive' && /^\/[^/]+\/[^/]+\/agents\/?$/.test(pathname)
}

export function convertChatRepoToAgentDropdownRepository(repo: CopilotChatRepo): AgentDropdownRepository | undefined {
  if (repo) {
    const nwo = `${repo.ownerLogin}/${repo.name}`
    return {
      nameWithOwner: nwo,
      name: nwo,
      nameOnly: repo.name,
      owner: repo.ownerLogin,
      defaultBranch: repo.defaultBranch,
      private: repo.visibility !== 'public',
      isEmpty: false,
      enabled: true,
      id: repo.id,
    }
  }
}
